import { useContext } from 'react';
import { FeatureFlagContext } from '../context/FeatureFlagContext';

/**
 * Custom hook for accessing feature flags with safe defaults
 *
 * This hook reads from the cached feature flag context - no network requests.
 * Flags are fetched once when FeatureFlagProvider mounts and cached in state.
 *
 * @param {string} flagKey - The feature flag key to check
 * @param {boolean} defaultValue - The default value if flag is not found (default: false)
 * @returns {boolean} Whether the feature flag is enabled
 *
 * @example
 * import { useFeatureFlag } from '../hooks/useFeatureFlag';
 * import { FEATURE_FLAGS } from '../config/featureFlags';
 *
 * const MyComponent = () => {
 *   const isNewFeatureEnabled = useFeatureFlag(FEATURE_FLAGS.NEW_FEATURE);
 *
 *   return (
 *     <div>
 *       {isNewFeatureEnabled && <NewFeature />}
 *     </div>
 *   );
 * };
 */
export const useFeatureFlag = (flagKey, defaultValue = false) => {
    const { flags, isLoading } = useContext(FeatureFlagContext);

    if (isLoading) return defaultValue;
    return flags[flagKey] ?? defaultValue;
};

/**
 * Custom hook for accessing multiple feature flags at once
 *
 * @param {string[]} flagKeys - Array of feature flag keys to check
 * @returns {Object} Object with flag keys as properties and boolean values
 *
 * @example
 * const { integrations_ui, new_dashboard } = useFeatureFlags([
 *   FEATURE_FLAGS.INTEGRATIONS_UI,
 *   FEATURE_FLAGS.NEW_DASHBOARD
 * ]);
 */
export const useFeatureFlags = (flagKeys) => {
    const { flags, isLoading } = useContext(FeatureFlagContext);

    return flagKeys.reduce((acc, key) => {
        acc[key] = isLoading ? false : (flags[key] ?? false);
        return acc;
    }, {});
};

/**
 * Hook to access the full feature flag context
 *
 * Use this when you need access to loading state, errors, or refetch
 *
 * @returns {Object} The full feature flag context
 *
 * @example
 * const { isLoading, error, refetch } = useFeatureFlagContext();
 */
export const useFeatureFlagContext = () => {
    return useContext(FeatureFlagContext);
};
